import type { FirmOverride, ScenarioPatch } from 'src/secret-menu/billing/hooks/useRevenue';

/**
 * Keeping the modeller's scenario as a patch over today. Nothing here prices anything: the back
 * end does that. These only make sure the patch holds real changes and nothing else, so "Showing
 * the current state" is true exactly when the figures are today's.
 */

type Setting = Exclude<keyof ScenarioPatch, 'firmOverrides'>;

const NAMES: Record<Setting, string> = {
  adviserFullAdoption: 'Adviser full adoption',
  monumentFullAdoption: 'Strategic partner full adoption',
  standardUnitPricePence: 'Standard price',
  includeMonument: 'Strategic partner excluded',
  monumentBuildFeeOn: 'Build fee',
  monumentBuildFeePence: 'Build fee',
  monumentLicenseFeePence: 'License fee',
  monumentCustomerCount: 'Strategic partner customers',
  includePipeline: 'Pipeline',
};

/** Set one assumption, or drop it when it is back at today's value. */
export function setAssumption<K extends Setting>(
  patch: ScenarioPatch,
  key: K,
  value: NonNullable<ScenarioPatch[K]>,
  today: NonNullable<ScenarioPatch[K]>,
): ScenarioPatch {
  const next = { ...patch };
  if (value === today) delete next[key];
  else next[key] = value;
  return next;
}

function withOverrides(
  patch: ScenarioPatch,
  overrides: Record<string, FirmOverride>,
): ScenarioPatch {
  const { firmOverrides: _replaced, ...rest } = patch;
  return Object.keys(overrides).length === 0 ? rest : { ...rest, firmOverrides: overrides };
}

/** Change one firm. Fields cleared back to the firm's own figure drop out, and so does an empty entry. */
export function setFirmOverride(
  patch: ScenarioPatch,
  companyId: string,
  change: FirmOverride,
): ScenarioPatch {
  const merged: FirmOverride = { ...patch.firmOverrides?.[companyId], ...change };
  const clean: FirmOverride = {};
  if (merged.excluded) clean.excluded = true;
  if (merged.units !== undefined) clean.units = merged.units;
  if (merged.unitPricePence !== undefined) clean.unitPricePence = merged.unitPricePence;

  const overrides = { ...patch.firmOverrides };
  if (Object.keys(clean).length === 0) delete overrides[companyId];
  else overrides[companyId] = clean;
  return withOverrides(patch, overrides);
}

/**
 * What the scenario changes, by name, for the line beside the Current state button. Firms are
 * counted from the overrides the back end applied, so the count matches the figures.
 */
export function changedAssumptions(patch: ScenarioPatch, firmsChanged: number): string[] {
  const names = new Set(
    (Object.keys(patch) as (keyof ScenarioPatch)[])
      .filter((key): key is Setting => key !== 'firmOverrides')
      .map(key => NAMES[key]),
  );
  if (firmsChanged > 0)
    names.add(`${firmsChanged} ${firmsChanged === 1 ? 'firm' : 'firms'} changed`);
  return [...names];
}
